<template>
  <div class="p-6">
    <h2 class="mb-1 text-lg font-semibold text-text-primary">
      {{ $t('W: Tilgængelige variabler') }}
    </h2>
    <p class="mb-6 text-sm text-text-primary">
      {{ $t('W: Indsæt variabler i tekstblokke med syntaksen') }}
      <code
        class="rounded bg-surface-muted px-1.5 py-1 font-mono text-xs text-text-primary">
        {{ variableTokenExample }}
      </code>
    </p>

    <!-- Account-tier variables -->
    <div class="mb-6">
      <div class="mb-2 flex items-center gap-2">
        <span class="text-sm font-medium text-text-primary">
          {{ $t('W: Laboratorieoplysninger') }}
        </span>
        <span class="rounded-full bg-surface-muted px-2 py-1 text-xs text-text-secondary">
          {{ $t('W: Automatisk') }}
        </span>
      </div>
      <p class="mb-3 text-xs text-text-primary">
        {{
          $t(
            'W: Disse værdier hentes automatisk fra jeres laboratoriekonto — de kan ikke redigeres her.'
          )
        }}
      </p>
      <div class="overflow-hidden rounded-lg border border-border-subtle">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-surface-muted border-b border-border-subtle">
              <th
                class="w-40 px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Token') }}
              </th>
              <th
                class="px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Beskrivelse') }}
              </th>
              <th
                class="w-56 px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Nuværende værdi') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-subtle">
            <tr
              v-for="variable in accountVariables"
              :key="variable.key"
              class="hover:bg-surface-muted">
              <td class="px-4 py-2.5">
                <div class="flex items-center gap-1">
                  <button
                    type="button"
                    class="cursor-pointer rounded-md p-1 text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                    @click="copyToken(variable.key)">
                    <IconLucideCheck
                      v-if="copiedKey === variable.key"
                      class="text-feedback-success h-3.5 w-3.5" />
                    <IconLucideCopy
                      v-else
                      class="h-3.5 w-3.5" />
                  </button>
                  <code
                    class="rounded bg-surface-muted px-1.5 py-1 font-mono text-xs text-text-primary"
                    v-text="'{{' + variable.key + '}}'"></code>
                </div>
              </td>
              <td class="px-4 py-2.5 text-text-primary">
                {{ descriptions[variable.key] ?? variable.description }}
              </td>
              <td class="px-4 py-2.5">
                <span
                  v-if="accountValues[variable.key]"
                  class="text-text-primary">
                  {{ accountValues[variable.key] }}
                </span>
                <span
                  v-else
                  class="text-text-muted italic">
                  {{ $t('W: Ikke udfyldt') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Order-tier variables -->
    <div>
      <div class="mb-2 flex items-center gap-2">
        <span class="text-sm font-medium text-text-primary">
          {{ $t('W: Ordreoplysninger') }}
        </span>
        <span
          class="bg-feedback-warning-bg text-feedback-warning rounded-full px-2 py-1 text-xs">
          {{ $t('W: Fra ordre') }}
        </span>
      </div>
      <p class="mb-3 text-xs text-text-primary">
        {{
          $t(
            "W: Disse værdier hentes fra den rigtige ordre når PDF'en genereres."
          )
        }}
      </p>
      <div class="overflow-hidden rounded-lg border border-border-subtle">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-surface-muted border-b border-border-subtle">
              <th
                class="w-40 px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Token') }}
              </th>
              <th
                class="px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Beskrivelse') }}
              </th>
              <th
                class="w-80 px-4 py-2.5 text-left text-xs font-medium text-text-primary">
                {{ $t('W: Eksempelværdi') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border-subtle">
            <tr
              v-for="variable in orderAndDerivedVariables"
              :key="variable.key"
              class="hover:bg-surface-muted">
              <td class="px-4 py-2.5">
                <div class="flex items-center gap-1">
                  <button
                    type="button"
                    class="cursor-pointer rounded-md p-1 text-text-secondary hover:bg-surface-muted hover:text-text-primary"
                    @click="copyToken(variable.key)">
                    <IconLucideCheck
                      v-if="copiedKey === variable.key"
                      class="text-feedback-success h-3.5 w-3.5" />
                    <IconLucideCopy
                      v-else
                      class="h-3.5 w-3.5" />
                  </button>
                  <code
                    class="rounded bg-surface-muted px-1.5 py-1 font-mono text-xs text-text-primary"
                    v-text="'{{' + variable.key + '}}'"></code>
                </div>
              </td>
              <td class="px-4 py-2.5 text-text-primary">
                {{ descriptions[variable.key] ?? variable.description }}
              </td>
              <td class="px-4 py-2.5">
                <span
                  v-if="variable.previewDefault || exampleValues[variable.key]"
                  class="whitespace-pre-line text-text-primary">
                  {{ variable.previewDefault || exampleValues[variable.key] }}
                </span>
                <span
                  v-else
                  class="text-text-muted italic">
                  {{ $t('W: Automatisk') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- DEV: copy all variable tokens as a newline-separated string -->
      <div
        class="mt-4 flex items-center justify-end border-t border-border-subtle pt-4">
        <button
          type="button"
          class="hover:bg-surface-muted flex cursor-pointer items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary"
          @click="copyAllTokens">
          <IconLucideCheck
            v-if="copiedAll"
            class="text-feedback-success h-3.5 w-3.5" />
          <IconLucideCopy
            v-else
            class="h-3.5 w-3.5" />
          {{ copiedAll ? $t('W: Kopieret') : $t('W: Kopier alle') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import { LAB_BASE_1_VARIABLES } from '@team-milva/lab-pdfs'
import { PhoneNumber } from '@team-milva/utils/phone'
import { formatReportDateSpan } from '@team-milva/lab-pdfs'
import { useAccountStore } from '@/stores/accountStore'
import type { ReportTemplate } from '@/stores/reportTemplateStore'

export default {
  name: 'ReportTemplateVariablesModal',

  emits: ['close'],

  props: {
    reportTemplate: {
      type: Object as PropType<ReportTemplate>,
      required: true,
    },
    locale: {
      type: String,
      default: 'da',
    },
  },

  data() {
    return {
      copiedKey: null as string | null,
      copiedAll: false,
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount']),

    descriptions(): Record<string, string> {
      return {
        labName: this.$t('W: Laboratoriets navn'),
        labPhone: this.$t('W: Laboratoriets telefonnummer'),
        labAddressLine: this.$t('W: Laboratoriets adresse'),
        labPostalCode: this.$t('W: Laboratoriets postnummer'),
        labCity: this.$t('W: Laboratoriets by'),
        sampleCount: this.$t('W: Antal prøver i ordren'),
        analysesCount: this.$t('W: Antal analyser i ordren'),
        samplesCountText: this.$t('W: Bruger sampleCount til at lave teksten.'),
        analysesCountText: this.$t(
          'W: Bruger analysesCount til at lave teksten.'
        ),
        customerName: this.$t('W: Kundens virksomhedsnavn'),
        confirmedDate: this.$t('W: Dato for ordrebekræftelse'),
        confirmedDateTime: this.$t('W: Dato og tidspunkt for ordrebekræftelse'),
        receivedDate: this.$t('W: Dato for modtagelse af prøverne'),
        analysedDate: this.$t('W: Dato for analyse af prøverne'),
        receivedDateText: this.$t('W: Dato for modtagelse, med "d." foran'),
        analysedDateText: this.$t('W: Dato for analyse, med "d." foran'),
        orderNumber: this.$t('W: Ordrenummer'),
        caseNumber: this.$t('W: Sagsnummer'),
        addressLine: this.$t('W: Ordrens adresse, postnummer og by kombineret'),
      }
    },

    variableTokenExample(): string {
      return '{{labName}}'
    },

    accountVariables() {
      return LAB_BASE_1_VARIABLES.filter((x) => x.tier === 'account')
    },

    orderAndDerivedVariables() {
      return LAB_BASE_1_VARIABLES.filter(
        (x) => x.tier === 'order' || x.tier === 'derived'
      )
    },

    accountValues(): Record<string, string> {
      const lab = this.labAccount
      const phone = lab?.primaryContact.phone
      return {
        labName: lab?.companyDetails.name ?? '',
        labPhone: phone?.number ? new PhoneNumber(phone).formatted : '',
        labAddressLine: lab?.address.addressLine ?? '',
        labPostalCode: lab?.address.postalCode ?? '',
        labCity: lab?.address.city ?? '',
      }
    },

    exampleValues(): Record<string, string> {
      const formatDate = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
      const confirmedDate = formatDate.format(new Date())
      // Received and analysed date show both forms: one date, and first - last when the samples
      // were received (or analysed) on several days. Based on today, like the other examples.
      const twoDaysAgo = new Date()
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
      const singleAndRangeExample = `${confirmedDate}\n${formatDate.format(twoDaysAgo)} - ${confirmedDate}`
      // The same two forms with the lead-in, written by the very function the report uses.
      const now = Date.now()
      const singleAndRangeTextExample = `${formatReportDateSpan([now], this.locale).text}\n${formatReportDateSpan([twoDaysAgo.getTime(), now], this.locale).text}`
      const confirmedDateTime = new Intl.DateTimeFormat(this.locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date())
      const sampleCount: number = 5
      const analysesCount: number = 8
      const samplesCountText =
        (
          {
            da:
              sampleCount === 1
                ? `${sampleCount} prøve`
                : `${sampleCount} prøver`,
            en:
              sampleCount === 1
                ? `${sampleCount} sample`
                : `${sampleCount} samples`,
            de:
              sampleCount === 1
                ? `${sampleCount} Probe`
                : `${sampleCount} Proben`,
          } as Record<string, string>
        )[this.locale] ?? `${sampleCount} prøver`
      const analysesCountText =
        (
          {
            da:
              analysesCount === 1
                ? `${analysesCount} analyse`
                : `${analysesCount} analyser`,
            en:
              analysesCount === 1
                ? `${analysesCount} analysis`
                : `${analysesCount} analyses`,
            de:
              analysesCount === 1
                ? `${analysesCount} Analyse`
                : `${analysesCount} Analysen`,
          } as Record<string, string>
        )[this.locale] ?? `${analysesCount} analyser`
      return {
        confirmedDate,
        confirmedDateTime,
        receivedDate: singleAndRangeExample,
        analysedDate: singleAndRangeExample,
        receivedDateText: singleAndRangeTextExample,
        analysedDateText: singleAndRangeTextExample,
        samplesCountText,
        analysesCountText,
      }
    },
  },

  methods: {
    copyToken(key: string): void {
      navigator.clipboard.writeText(`{{${key}}}`).catch(() => {})
      this.copiedKey = key
      setTimeout(() => {
        this.copiedKey = null
      }, 1500)
    },

    // DEV: copy all variable tokens as a newline-separated string for quick paste testing
    copyAllTokens(): void {
      const tokens = LAB_BASE_1_VARIABLES.map((x) => `{{${x.key}}}`).join('\n')
      navigator.clipboard.writeText(tokens).catch(() => {})
      this.copiedAll = true
      setTimeout(() => {
        this.copiedAll = false
      }, 1500)
    },
  },
}
</script>

